import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import { faSave } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ChangeEvent, type ReactElement, useState } from 'react'
import { Form, TextInput } from '~/components/Form'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { themeClass } from '~/styles/theme.css'
import { popover, saveFilterForm } from './FilterBar.css'

type SaveFilterButtonProps = {
  onSave: (name: string) => void
}

export const SaveFilterButton = ({
  onSave
}: SaveFilterButtonProps): ReactElement => {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (v) => {
      setOpen(v)
      if (!v) setName('')
    },
    strategy: 'fixed',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start'
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss
  ])
  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  const handleSubmit = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    onSave(trimmed)
    setOpen(false)
    setName('')
  }

  return (
    <>
      <span ref={refs.setReference} {...getReferenceProps()}>
        <Tooltip text="Sauvegarder les filtres" direction="top" asChild>
          <Button
            variation={ButtonVariation.PrimaryInversed}
            buttonSize="small"
            narrow
            type="button"
            aria-label="Sauvegarder les filtres"
          >
            <FontAwesomeIcon icon={faSave} />
          </Button>
        </Tooltip>
      </span>
      <FloatingPortal>
        {isMounted && (
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 99 }}
            {...getFloatingProps()}
          >
            <div className={clsx(themeClass, popover)} style={transitionStyles}>
              <Form onSubmit={handleSubmit}>
                <div className={saveFilterForm}>
                  <TextInput
                    name="savedSearchName"
                    placeholder="Nom de la recherche"
                    autoFocus
                    small
                    standalone
                    value={name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setName(e.target.value)
                    }
                  />
                  <Button
                    type="submit"
                    variation={ButtonVariation.Primary}
                    disabled={!name.trim()}
                    narrow
                    buttonSize="small"
                  >
                    <FontAwesomeIcon icon={faSave} />
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  )
}
